// ============================================================
// planRenderer.js — the floor plan as vector SVG, fitted into a box on a
// drawing sheet (all output in PDF points)
//
// Draws, back to front: drafting grid, rooms, clearance zones, floor
// drains, equipment drawings, walls (with door openings), doors,
// dimensions, equipment labels + reference markers, north arrow, scale bar.
//
// Uses the same layout data as the editor (shop vertices/walls, doors,
// placed items, drains) and never changes it. Positions come straight
// from item cx/cy/rot/fw/fh/scale, so the plan matches the editor exactly.
// ============================================================

import { getDoorEndpoints, computeBbox } from '../utils/shopGeometry';
import { CATALOG } from '../data/catalog';
import { artMarkup, hasArt, artFit } from './equipmentArt';
import { textWidth, esc, ftIn } from './textMetrics';

const FONT = 'Helvetica, Arial, sans-serif';

// Standard architectural scales (pt per real foot). The plan uses the
// largest one that fits, unless that wastes too much of the sheet — then
// it fits exactly and the scale is stated as a ratio.
const ARCH_SCALES = [
  { k: 36, label: '1/2" = 1\'-0"' }, { k: 27, label: '3/8" = 1\'-0"' },
  { k: 18, label: '1/4" = 1\'-0"' }, { k: 13.5, label: '3/16" = 1\'-0"' },
  { k: 9, label: '1/8" = 1\'-0"' }, { k: 6.75, label: '3/32" = 1\'-0"' },
  { k: 4.5, label: '1/16" = 1\'-0"' }, { k: 2.25, label: '1/32" = 1\'-0"' },
];
function pickScale(kFit) {
  const std = ARCH_SCALES.find((s) => s.k <= kFit);
  if (std && std.k >= kFit * 0.85) return { k: std.k, label: std.label, standard: true };
  return { k: kFit, label: `1:${Math.round(864 / kFit)}`, standard: false };
}

// Rotated rectangle → its 4 corners and axis-aligned box, in feet
function itemGeom(item) {
  const w = (item.fw || 0) * (item.scaleX || 1), d = (item.fh || 0) * (item.scaleY || 1);
  const r = ((item.rot || 0) * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  const pts = [[-w / 2, -d / 2], [w / 2, -d / 2], [w / 2, d / 2], [-w / 2, d / 2]]
    .map(([x, y]) => ({ x: item.cx + x * c - y * s, y: item.cy + x * s + y * c }));
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  return { w, d, pts, minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

const overlaps = (a, b, pad = 0) =>
  a.x < b.x + b.w + pad && a.x + a.w + pad > b.x && a.y < b.y + b.h + pad && a.y + a.h + pad > b.y;

// Signed area > 0 → vertices run clockwise on screen (y down)
const signedArea = (v) => v.reduce((s, p, i) => {
  const q = v[(i + 1) % v.length];
  return s + p.x * q.y - q.x * p.y;
}, 0) / 2;

export function renderPlan({ shop, doors = [], placed = [], drains = [], refByUid = {}, opts, theme, box }) {
  const out = [];
  const verts = shop.vertices || [];
  const sb = computeBbox(verts);
  const items = placed.filter((p) => !p.isRoom);
  const rooms = placed.filter((p) => p.isRoom);

  // Drawing extents: the shop plus anything placed outside its walls
  let minX = sb.minX, minY = sb.minY, maxX = sb.maxX, maxY = sb.maxY;
  placed.forEach((p) => {
    const g = itemGeom(p);
    minX = Math.min(minX, g.minX); minY = Math.min(minY, g.minY);
    maxX = Math.max(maxX, g.maxX); maxY = Math.max(maxY, g.maxY);
  });
  const isRect = (shop.shapeType || 'rect') !== 'free';

  // Room around the plan for dimensions, door tags and the north arrow
  const dimOn = opts.showDimensions;
  const mL = dimOn ? 40 : 18, mT = dimOn ? 40 : 30, mB = dimOn && !isRect ? 40 : 30;
  const fit = (mR) => {
    const availW = box.w - mL - mR, availH = box.h - mT - mB;
    const sc = pickScale(Math.min(availW / (maxX - minX), availH / (maxY - minY)));
    const pw = (maxX - minX) * sc.k, ph = (maxY - minY) * sc.k;
    return { sc, pw, ox: box.x + mL + (availW - pw) / 2, oy: box.y + mT + (availH - ph) / 2 };
  };
  // Tight right margin first; if the plan then runs under the north arrow
  // (top-right corner), refit with room for it. Either way it always fits.
  let fitR = fit(dimOn && !isRect ? 40 : 16);
  if (opts.showNorth && fitR.ox + fitR.pw > box.x + box.w - 38 && fitR.oy - (dimOn ? 32 : 4) < box.y + 44) {
    fitR = fit(dimOn && !isRect ? 48 : 44);
  }
  const scale = fitR.sc;
  const k = scale.k;
  const { ox, oy } = fitR;
  const X = (x) => ox + (x - minX) * k;
  const Y = (y) => oy + (y - minY) * k;
  const f = (n) => n.toFixed(2);

  // ─── Drafting grid (5 ft, aligned to the shop's own origin) ────────────
  if (opts.showGrid) {
    const step = 5, lines = [];
    for (let gx = Math.ceil(sb.minX / step) * step; gx <= sb.maxX + 1e-6; gx += step) lines.push(`M${f(X(gx))} ${f(Y(sb.minY))}V${f(Y(sb.maxY))}`);
    for (let gy = Math.ceil(sb.minY / step) * step; gy <= sb.maxY + 1e-6; gy += step) lines.push(`M${f(X(sb.minX))} ${f(Y(gy))}H${f(X(sb.maxX))}`);
    out.push(`<path d="${lines.join('')}" fill="none" stroke="${theme.grid}" stroke-width="0.35"/>`);
  }

  const reserved = [];   // text boxes (room names, dimensions) tags must avoid

  // ─── Rooms ─────────────────────────────────────────────────────────────
  rooms.forEach((rm) => {
    let d, cx = rm.cx, cy = rm.cy, wFt, dFt;
    if (rm.shapeType === 'free' && Array.isArray(rm.vertices) && rm.vertices.length >= 3) {
      d = `M${rm.vertices.map((p) => `${f(X(p.x))} ${f(Y(p.y))}`).join('L')}Z`;
      const rb = computeBbox(rm.vertices); wFt = rb.w; dFt = rb.h;
      cx = rb.minX + rb.w / 2; cy = rb.minY + rb.h / 2;
    } else {
      const g = itemGeom(rm); wFt = g.w; dFt = g.d;
      d = `M${g.pts.map((p) => `${f(X(p.x))} ${f(Y(p.y))}`).join('L')}Z`;
    }
    out.push(`<path d="${d}" fill="${theme.roomFill}" stroke="${theme.roomLine}" stroke-width="0.7" stroke-dasharray="3 1.5"/>`);
    const name = (rm.label || rm.name || 'Room').toUpperCase();
    const ns = Math.min(8, Math.max(5.5, wFt * k * 0.09));
    const nw = Math.max(textWidth(name, ns, true), opts.showRoomDimensions ? textWidth(`${ftIn(wFt)} × ${ftIn(dFt)}`, ns * 0.8) : 0);
    reserved.push({ x: X(cx) - nw / 2, y: Y(cy) - ns - 1, w: nw, h: ns * 2.3 });
    out.push(`<text x="${f(X(cx))}" y="${f(Y(cy) - (opts.showRoomDimensions ? 1 : -ns / 3))}" font-family="${FONT}" font-size="${ns}" font-weight="bold" fill="${theme.text}" text-anchor="middle">${esc(name)}</text>`);
    if (opts.showRoomDimensions) {
      out.push(`<text x="${f(X(cx))}" y="${f(Y(cy) + ns)}" font-family="${FONT}" font-size="${ns * 0.8}" fill="${theme.muted}" text-anchor="middle">${esc(`${ftIn(wFt)} × ${ftIn(dFt)}`)}</text>`);
    }
  });

  // ─── Lift clearance zones (optional) ───────────────────────────────────
  if (opts.showClearance) {
    items.forEach((it) => {
      const cat = CATALOG.find((c) => c.id === it.id);
      const fp = it.bayFootprint || cat?.bayFootprint;
      if (!fp) return;
      const z = itemGeom({ ...it, fw: fp.width, fh: fp.depth, scaleX: 1, scaleY: 1 });
      out.push(`<path d="M${z.pts.map((p) => `${f(X(p.x))} ${f(Y(p.y))}`).join('L')}Z" fill="${theme.clearFill}" stroke="${theme.clearLine}" stroke-width="0.5" stroke-dasharray="4 2"/>`);
    });
  }

  // ─── Floor drains ──────────────────────────────────────────────────────
  drains.forEach((dr) => {
    const w = (dr.w || 2) * k, h = (dr.h || 2) * k;
    const x = X(dr.cx) - w / 2, y = Y(dr.cy) - h / 2;
    out.push(`<rect x="${f(x)}" y="${f(y)}" width="${f(w)}" height="${f(h)}" fill="none" stroke="${theme.muted}" stroke-width="0.5"/>`);
    out.push(`<path d="M${f(x)} ${f(y)}L${f(x + w)} ${f(y + h)}M${f(x + w)} ${f(y)}L${f(x)} ${f(y + h)}" stroke="${theme.muted}" stroke-width="0.35"/>`);
  });

  // ─── Equipment drawings ────────────────────────────────────────────────
  const itemBoxes = [];   // pt AABBs, for label/marker placement
  items.forEach((it) => {
    const g = itemGeom(it);
    const wPt = g.w * k, dPt = g.d * k;
    const t = `translate(${f(X(it.cx))},${f(Y(it.cy))}) rotate(${it.rot || 0})`;
    if (hasArt(it.id)) {
      out.push(`<g transform="${t}">${artMarkup(it.id, wPt, dPt, theme.equip, theme.equipLine)}</g>`);
    } else {
      // No drawing (obstruction / column etc.): outline + cross
      out.push(`<g transform="${t}"><rect x="${f(-wPt / 2)}" y="${f(-dPt / 2)}" width="${f(wPt)}" height="${f(dPt)}" fill="none" stroke="${theme.equip}" stroke-width="0.7"/>`
        + `<path d="M${f(-wPt / 2)} ${f(-dPt / 2)}L${f(wPt / 2)} ${f(dPt / 2)}M${f(wPt / 2)} ${f(-dPt / 2)}L${f(-wPt / 2)} ${f(dPt / 2)}" stroke="${theme.equip}" stroke-width="0.4"/></g>`);
    }
    // Tags hug what's actually drawn (a drawing can be narrower than its
    // footprint), so a marker never floats in empty floor next to its item
    const fit = hasArt(it.id) ? artFit(it.id, wPt, dPt) : { w: wPt, h: dPt };
    const r = ((it.rot || 0) * Math.PI) / 180;
    const bw = Math.abs(fit.w * Math.cos(r)) + Math.abs(fit.h * Math.sin(r));
    const bh = Math.abs(fit.w * Math.sin(r)) + Math.abs(fit.h * Math.cos(r));
    itemBoxes.push({ uid: it.uid, x: X(it.cx) - bw / 2, y: Y(it.cy) - bh / 2, w: bw, h: bh });
  });

  // ─── Walls with door openings ──────────────────────────────────────────
  const walls = shop.walls || [];
  const cw = signedArea(verts) > 0;       // orientation → outward normal side
  const wallParts = [];
  const doorGeo = doors.map((dr) => ({ dr, e: getDoorEndpoints(dr, shop) })).filter((x) => x.e);
  walls.forEach((wall) => {
    const a = verts[wall.startVertex], b = verts[wall.endVertex];
    if (!a || !b) return;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (!len) return;
    const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
    const gaps = doors.filter((dr) => dr.wallId === wall.id).map((dr) => {
      const s0 = Math.max(0, Math.min(len - dr.width, dr.offset || 0));
      return [s0, s0 + dr.width];
    }).sort((p, q) => p[0] - q[0]);
    let t = 0;
    const at = (s) => ({ x: a.x + ux * s, y: a.y + uy * s });
    const seg = (s0, s1) => { const p = at(s0), q = at(s1); wallParts.push(`M${f(X(p.x))} ${f(Y(p.y))}L${f(X(q.x))} ${f(Y(q.y))}`); };
    gaps.forEach(([g0, g1]) => { if (g0 > t) seg(t, g0); t = Math.max(t, g1); });
    if (t < len) seg(t, len);
  });
  out.push(`<path d="${wallParts.join('')}" fill="none" stroke="${theme.wall}" stroke-width="${theme.wallPt}" stroke-linecap="square"/>`);

  // ─── Doors ─────────────────────────────────────────────────────────────
  const tagBoxes = [];
  doorGeo.forEach(({ dr, e }) => {
    const sx = X(e.start.x), sy = Y(e.start.y), ex = X(e.end.x), ey = Y(e.end.y);
    const inX = -e.outwardVec.x, inY = -e.outwardVec.y;
    const wPt = Math.hypot(ex - sx, ey - sy);
    // Jamb ticks at both sides of the opening
    const jt = theme.wallPt * 1.6;
    out.push(`<path d="M${f(sx - inX * jt / 2)} ${f(sy - inY * jt / 2)}l${f(inX * jt)} ${f(inY * jt)}M${f(ex - inX * jt / 2)} ${f(ey - inY * jt / 2)}l${f(inX * jt)} ${f(inY * jt)}" stroke="${theme.wall}" stroke-width="0.9"/>`);
    if (dr.type === 'overhead') {
      // Overhead: dashed head line across the opening + a parallel track line inside
      out.push(`<line x1="${f(sx)}" y1="${f(sy)}" x2="${f(ex)}" y2="${f(ey)}" stroke="${theme.door}" stroke-width="1.1" stroke-dasharray="4 2"/>`);
      const o = Math.min(10, 1.5 * k);
      out.push(`<line x1="${f(sx + inX * o)}" y1="${f(sy + inY * o)}" x2="${f(ex + inX * o)}" y2="${f(ey + inY * o)}" stroke="${theme.door}" stroke-width="0.4" stroke-dasharray="1.5 1.5"/>`);
    } else {
      // Walk door: leaf + 90° swing arc into the shop
      const lx = sx + inX * wPt, ly = sy + inY * wPt;
      out.push(`<line x1="${f(sx)}" y1="${f(sy)}" x2="${f(lx)}" y2="${f(ly)}" stroke="${theme.door}" stroke-width="0.8"/>`);
      const sweep = ((ex - sx) * inY - (ey - sy) * inX) > 0 ? 0 : 1;
      out.push(`<path d="M${f(ex)} ${f(ey)}A${f(wPt)} ${f(wPt)} 0 0 ${sweep} ${f(lx)} ${f(ly)}" fill="none" stroke="${theme.door}" stroke-width="0.45" stroke-dasharray="2 1.2"/>`);
    }
    if (opts.showDoorTags) {
      // Tag outside the wall: "12'-0" OH" / "4'-0" WD"
      const label = `${ftIn(dr.width)} ${dr.type === 'overhead' ? 'OH' : 'WD'}`;
      const mx = (sx + ex) / 2 - inX * 9, my = (sy + ey) / 2 - inY * 9;
      const ang = Math.atan2(ey - sy, ex - sx) * 180 / Math.PI;
      const a2 = ang > 90 || ang < -90 ? ang + 180 : ang;
      out.push(`<text x="${f(mx)}" y="${f(my + 2)}" font-family="${FONT}" font-size="5.5" fill="${theme.muted}" text-anchor="middle" transform="rotate(${f(a2)} ${f(mx)} ${f(my)})">${esc(label)}</text>`);
      const tw = textWidth(label, 5.5);
      tagBoxes.push({ x: mx - tw / 2, y: my - 5, w: tw, h: 9 });
    }
  });

  // ─── Dimensions ────────────────────────────────────────────────────────
  const dimLine = (p, q, nx, ny, off, label) => {
    // p→q measured along a wall; dimension line offset `off` pt along (nx, ny)
    const p1 = { x: p.x + nx * off, y: p.y + ny * off }, q1 = { x: q.x + nx * off, y: q.y + ny * off };
    const ext = (pt, pt1) => `M${f(pt.x + nx * 3)} ${f(pt.y + ny * 3)}L${f(pt1.x + nx * 3)} ${f(pt1.y + ny * 3)}`;
    const len = Math.hypot(q.x - p.x, q.y - p.y), ux = (q.x - p.x) / len, uy = (q.y - p.y) / len;
    const tick = (c) => `M${f(c.x - (ux + nx) * 2.5)} ${f(c.y - (uy + ny) * 2.5)}L${f(c.x + (ux + nx) * 2.5)} ${f(c.y + (uy + ny) * 2.5)}`;
    out.push(`<path d="${ext(p, p1)}${ext(q, q1)}M${f(p1.x)} ${f(p1.y)}L${f(q1.x)} ${f(q1.y)}" fill="none" stroke="${theme.dim}" stroke-width="0.45"/>`);
    out.push(`<path d="${tick(p1)}${tick(q1)}" stroke="${theme.dim}" stroke-width="0.9"/>`);
    const mx = (p1.x + q1.x) / 2, my = (p1.y + q1.y) / 2;
    const ang = Math.atan2(uy, ux) * 180 / Math.PI;
    const a2 = ang > 90 || ang <= -90 ? ang + 180 : ang;
    const tw = textWidth(label, 7.5, true) + 6;
    // Reserve the label's (rotated) footprint so tags don't land on it
    const rw = Math.abs(tw * Math.cos(ang * Math.PI / 180)) + 10 * Math.abs(Math.sin(ang * Math.PI / 180));
    const rh = Math.abs(tw * Math.sin(ang * Math.PI / 180)) + 10 * Math.abs(Math.cos(ang * Math.PI / 180));
    reserved.push({ x: mx - rw / 2, y: my - rh / 2, w: rw, h: rh });
    out.push(`<g transform="rotate(${f(a2)} ${f(mx)} ${f(my)})"><rect x="${f(mx - tw / 2)}" y="${f(my - 5)}" width="${f(tw)}" height="10" fill="${theme.paper}"/>`
      + `<text x="${f(mx)}" y="${f(my + 2.7)}" font-family="${FONT}" font-size="7.5" font-weight="bold" fill="${theme.dimText}" text-anchor="middle">${esc(label)}</text></g>`);
  };
  if (dimOn && verts.length >= 3) {
    if (isRect) {
      const tl = { x: X(sb.minX), y: Y(sb.minY) }, tr = { x: X(sb.maxX), y: Y(sb.minY) }, bl = { x: X(sb.minX), y: Y(sb.maxY) };
      // Offset clears anything drawn outside the walls on that side
      dimLine(tl, tr, 0, -1, Y(sb.minY) - Y(minY) + 22, ftIn(sb.w));
      dimLine(bl, tl, -1, 0, X(sb.minX) - X(minX) + 22, ftIn(sb.h));
    } else {
      walls.forEach((wall) => {
        const a = verts[wall.startVertex], b = verts[wall.endVertex];
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 1) return;
        let nx = (b.y - a.y) / len, ny = -(b.x - a.x) / len;   // left normal (y-down)
        if (!cw) { nx = -nx; ny = -ny; }
        dimLine({ x: X(a.x), y: Y(a.y) }, { x: X(b.x), y: Y(b.y) }, nx, ny, 20, ftIn(len));
      });
    }
  }

  // ─── Labels and reference markers ──────────────────────────────────────
  const taken = [...tagBoxes, ...reserved];
  const labelOut = [];
  const fits = (r, selfUid) => r.x >= box.x && r.y >= box.y && r.x + r.w <= box.x + box.w && r.y + r.h <= box.y + box.h
    && !taken.some((t) => overlaps(r, t, 1))
    && !itemBoxes.some((b) => b.uid !== selfUid && overlaps(r, b, 0.5));
  // Biggest items first, so the clearest positions go to what's easiest to read
  const order = [...items].sort((a, b) => (b.fw * b.fh) - (a.fw * a.fh));
  order.forEach((it) => {
    const ib = itemBoxes.find((b) => b.uid === it.uid);
    if (!ib) return;
    const cxp = ib.x + ib.w / 2, cyp = ib.y + ib.h / 2;
    const ref = refByUid[it.uid];

    if (opts.showRefs && ref) {
      const r = theme.markerR;
      // Preferred spots: centred above, then the corners, then the sides
      const cands = [
        [cxp, ib.y - r - 2],
        [ib.x + ib.w + r + 1.5, ib.y - r - 1.5], [ib.x - r - 1.5, ib.y - r - 1.5],
        [ib.x + ib.w + r + 1.5, ib.y + ib.h + r + 1.5], [ib.x - r - 1.5, ib.y + ib.h + r + 1.5],
        [ib.x + ib.w + r + 2, cyp], [cxp, ib.y + ib.h + r + 2], [ib.x - r - 2, cyp],
        [ib.x + ib.w + r + 8, ib.y - r - 8], [ib.x - r - 8, ib.y - r - 8],
      ];
      let pos = cands.find(([x, y]) => fits({ x: x - r, y: y - r, w: r * 2, h: r * 2 }, it.uid));
      if (!pos) pos = [cxp, cyp];
      const [mx, my] = pos;
      taken.push({ x: mx - r, y: my - r, w: r * 2, h: r * 2 });
      // Leader to the item when the tag sits outside it
      const inside = mx > ib.x && mx < ib.x + ib.w && my > ib.y && my < ib.y + ib.h;
      if (!inside) {
        const tx = Math.max(ib.x, Math.min(mx, ib.x + ib.w)), ty = Math.max(ib.y, Math.min(my, ib.y + ib.h));
        const dl = Math.hypot(tx - mx, ty - my);
        if (dl > r + 1) {
          labelOut.push(`<line x1="${f(mx + (tx - mx) / dl * r)}" y1="${f(my + (ty - my) / dl * r)}" x2="${f(tx)}" y2="${f(ty)}" stroke="${theme.marker}" stroke-width="0.45"/>`);
          labelOut.push(`<circle cx="${f(tx)}" cy="${f(ty)}" r="0.9" fill="${theme.marker}"/>`);
        }
      }
      labelOut.push(`<circle cx="${f(mx)}" cy="${f(my)}" r="${r}" fill="${theme.paper}" stroke="${theme.marker}" stroke-width="0.8"/>`);
      labelOut.push(`<text x="${f(mx)}" y="${f(my + r * 0.36)}" font-family="${FONT}" font-size="${f(r * 1.0)}" font-weight="bold" fill="${theme.markerText}" text-anchor="middle">${ref}</text>`);
    }

    if (opts.showLabels) {
      const name = (it.label || CATALOG.find((c) => c.id === it.id)?.name || it.name || '').toUpperCase();
      // Busy plans: step the text down until it finds a clear spot. It's
      // vector, so even small text stays sharp when the PDF is zoomed in.
      let placedLabel = null;
      for (const fs of [theme.labelPt, theme.labelPt * 0.8, theme.labelPt * 0.62, theme.labelPt * 0.48]) {
        const tw = textWidth(name, fs, true), th = fs + 2;
        const cands = [
          [cxp, ib.y + ib.h + 2], [cxp, ib.y - th - 2], [ib.x + ib.w + 3 + tw / 2, cyp - th / 2],
          [ib.x - 3 - tw / 2, cyp - th / 2], [cxp, ib.y + ib.h + th + 4], [cxp, ib.y - 2 * th - 4],
        ];
        const pos = cands.find(([x, y]) => fits({ x: x - tw / 2, y, w: tw, h: th }, it.uid));
        if (pos) { placedLabel = { fs, tw, th, lx: pos[0], ly: pos[1] }; break; }
      }
      if (!placedLabel) {
        // Nowhere clear: smallest size, centred on the item, kept on the sheet
        const fs = theme.labelPt * 0.48, tw = textWidth(name, fs, true), th = fs + 2;
        const lx = Math.min(box.x + box.w - tw / 2, Math.max(box.x + tw / 2, cxp));
        const ly = Math.min(box.y + box.h - th, Math.max(box.y, cyp - th / 2));
        placedLabel = { fs, tw, th, lx, ly };
      }
      const { fs, tw, th, lx, ly } = placedLabel;
      taken.push({ x: lx - tw / 2, y: ly, w: tw, h: th });
      labelOut.push(`<text x="${f(lx)}" y="${f(ly + fs)}" font-family="${FONT}" font-size="${f(fs)}" font-weight="bold" fill="${theme.text}" text-anchor="middle">${esc(name)}</text>`);
    }
  });
  out.push(...labelOut);

  // ─── North arrow (top-right of the drawing area) ───────────────────────
  if (opts.showNorth) {
    const nx = box.x + box.w - 20, ny = box.y + 28;   // sits in the top-right margin
    out.push(`<circle cx="${nx}" cy="${ny}" r="11" fill="none" stroke="${theme.wall}" stroke-width="0.6"/>`
      + `<path d="M${nx} ${ny - 10}L${nx + 5} ${ny + 6}L${nx} ${ny + 3}Z" fill="${theme.wall}"/>`
      + `<path d="M${nx} ${ny - 10}L${nx - 5} ${ny + 6}L${nx} ${ny + 3}Z" fill="none" stroke="${theme.wall}" stroke-width="0.6"/>`
      + `<text x="${nx}" y="${ny - 13.5}" font-family="${FONT}" font-size="8" font-weight="bold" fill="${theme.text}" text-anchor="middle">N</text>`);
  }

  // ─── Graphic scale bar (bottom-left of the drawing area) ───────────────
  if (opts.showScaleBar) {
    const stepFt = [1, 2, 5, 10, 20, 25, 50].find((s) => s * k * 4 >= 110) || 50;
    const sx = box.x + 6, sy = box.y + box.h - 14, seg = stepFt * k;
    let bar = '';
    for (let i = 0; i < 4; i++) {
      bar += `<rect x="${f(sx + i * seg)}" y="${f(sy)}" width="${f(seg)}" height="3.5" fill="${i % 2 ? theme.paper : theme.wall}" stroke="${theme.wall}" stroke-width="0.5"/>`;
    }
    for (let i = 0; i <= 4; i++) {
      bar += `<text x="${f(sx + i * seg)}" y="${f(sy - 2.5)}" font-family="${FONT}" font-size="5.5" fill="${theme.text}" text-anchor="middle">${i * stepFt}</text>`;
    }
    bar += `<text x="${f(sx + 4 * seg + 5)}" y="${f(sy + 3.5)}" font-family="${FONT}" font-size="5.5" fill="${theme.text}">FT</text>`;
    bar += `<text x="${f(sx)}" y="${f(sy + 11)}" font-family="${FONT}" font-size="6" font-weight="bold" fill="${theme.text}">SCALE ${esc(scale.label)}</text>`;
    out.push(bar);
  }

  return { svg: out.join('\n'), scale };
}
